import * as Sentry from '@sentry/react'
import { normalizeLegacyAttribution } from './legacyAttribution'

const PROJECT_ID = 'prj_b02be1f1748fc86bcf9c'
const PUBLIC_KEY = 'c2abdb60587648734c1f3968c2d9ed79'
const ENVELOPE_URL = `https://glitchreplay.com/api/${PROJECT_ID}/envelope/?sentry_key=${PUBLIC_KEY}`

const ANON_KEY = 'oc_anon_id'
const SESSION_KEY = 'oc_session_id'
const FIRST_NOTE_KEY = 'oc_activated'
const ATTR_COOKIE = 'oc_attr'
const SHARED_COOKIE_MAX_AGE = 60 * 60 * 24 * 400
const SHARED_DOMAIN = 'omnicanvasnotes.com'

export type AnalyticsProps = Record<string, unknown>
export type AnalyticsSource = 'app' | 'marketing'

export interface AnalyticsUser {
  id?: string | null
  email?: string | null
}

export interface ProductAnalytics {
  identify: (user: AnalyticsUser | null) => void
  track: (name: string, props?: AnalyticsProps) => void
  trackOnce: (key: string, name: string, props?: AnalyticsProps) => boolean
  trackSessionOnce: (key: string, name: string, props?: AnalyticsProps) => boolean
}

/**
 * Accounts belonging to whoever builds and operates OmniCanvas. Their usage is
 * real but it is not customer behaviour, and at current volumes it swamps the
 * signal: `ai_feature_used` logged 1,560 events against 34 identities, so a
 * handful of operator sessions decided what the feature-usage numbers said.
 * Events still send — they are stamped `internal: true` so a query can drop
 * them, which is recoverable in a way that not sending them is not.
 */
const INTERNAL_EMAIL_DOMAINS = ['inventivehq.com', 'econroy.com', 'omnicanvasnotes.com']

export function isInternalEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const at = email.lastIndexOf('@')
  if (at < 0) return false
  return INTERNAL_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase())
}

const hasWindow = typeof window !== 'undefined'

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID().replace(/-/g, '')
    }
  } catch {
    /* fall through */
  }
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join('')
}

function readCookie(name: string): string | null {
  if (!hasWindow) return null
  try {
    const prefix = `${name}=`
    const match = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(prefix))
    return match ? decodeURIComponent(match.slice(prefix.length)) : null
  } catch {
    return null
  }
}

function sharedCookieAttributes(): string {
  if (!hasWindow) return ''
  const hostname = window.location.hostname.toLowerCase()
  const domain = hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)
    ? `; Domain=.${SHARED_DOMAIN}`
    : ''
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  return `; Path=/; Max-Age=${SHARED_COOKIE_MAX_AGE}; SameSite=Lax${domain}${secure}`
}

function writeSharedCookie(name: string, value: string): void {
  if (!hasWindow) return
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}${sharedCookieAttributes()}`
  } catch {
    /* cookies can be disabled without breaking the product */
  }
}

function validAnonymousId(value: string | null): value is string {
  return !!value && value.length >= 16 && value.length <= 80 && /^[a-zA-Z0-9_-]+$/.test(value)
}

/**
 * Returns the same pseudonymous visitor id on the marketing and app
 * subdomains. localStorage remains a fallback for environments that block
 * cookies, while the parent-domain cookie is what joins the two origins.
 */
export function getAnonymousId(): string {
  if (!hasWindow) return 'ssr'
  try {
    const cookieId = readCookie(ANON_KEY)
    const storedId = localStorage.getItem(ANON_KEY)
    let id = validAnonymousId(cookieId)
      ? cookieId
      : validAnonymousId(storedId)
        ? storedId
        : uuid()
    if (storedId !== id) localStorage.setItem(ANON_KEY, id)
    if (cookieId !== id) writeSharedCookie(ANON_KEY, id)
    return id
  } catch {
    const cookieId = readCookie(ANON_KEY)
    if (validAnonymousId(cookieId)) return cookieId
    const id = uuid()
    writeSharedCookie(ANON_KEY, id)
    return id
  }
}

function safeValue(value: string | null, maxLength = 500): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed.slice(0, maxLength) : undefined
}

function parseAttributionCookie(): AnalyticsProps {
  const out: AnalyticsProps = {}
  const raw = readCookie(ATTR_COOKIE)
  if (!raw) return out
  try {
    const parsed = JSON.parse(raw)
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'referrer', 'landing_page', 'captured_at']) {
      if (typeof parsed?.[key] === 'string' && parsed[key]) out[key] = parsed[key]
    }
  } catch {
    /* ignore malformed cookie */
  }
  // Cookies live 400 days, so first touches written under the old free-tool
  // scheme keep arriving long after the links were changed. Map them forward
  // so old and new visitors land in the same series.
  return normalizeLegacyAttribution(out)
}

function isInternalReferrer(referrer: string): boolean {
  try {
    const hostname = new URL(referrer).hostname.toLowerCase()
    return hostname === SHARED_DOMAIN || hostname.endsWith(`.${SHARED_DOMAIN}`)
  } catch {
    return false
  }
}

/**
 * Captures first-touch attribution on whichever OmniCanvas origin the visitor
 * reaches first. This matters for direct blog → app journeys: the app's CTA
 * query can now establish attribution even when the marketing landing page had
 * no external referrer.
 */
export function captureFirstTouchAttribution(): AnalyticsProps {
  if (!hasWindow) return {}
  const existing = parseAttributionCookie()
  if (Object.keys(existing).length > 0) return existing

  try {
    const query = new URLSearchParams(window.location.search)
    const utmSource = safeValue(query.get('utm_source'), 100)
    const utmMedium = safeValue(query.get('utm_medium'), 100)
    const utmCampaign = safeValue(query.get('utm_campaign'), 200)
    const utmContent = safeValue(query.get('utm_content'), 200)
    const referrer = safeValue(document.referrer)
    const externalReferrer = referrer && !isInternalReferrer(referrer) ? referrer : undefined

    if (!utmSource && !utmMedium && !utmCampaign && !utmContent && !externalReferrer) {
      return {}
    }

    const captured: AnalyticsProps = {
      ...(utmSource ? { utm_source: utmSource } : {}),
      ...(utmMedium ? { utm_medium: utmMedium } : {}),
      ...(utmCampaign ? { utm_campaign: utmCampaign } : {}),
      ...(utmContent ? { utm_content: utmContent } : {}),
      ...(externalReferrer ? { referrer: externalReferrer } : {}),
      landing_page: `${window.location.pathname}${window.location.search}`.slice(0, 1000),
      captured_at: new Date().toISOString(),
    }
    writeSharedCookie(ATTR_COOKIE, JSON.stringify(captured))
    // Store the raw first touch, report the normalized one: a legacy link that
    // is still in the wild (a bookmark, an old shared URL) can otherwise seed a
    // brand-new cookie with the retired shape.
    return normalizeLegacyAttribution(captured)
  } catch {
    return {}
  }
}

function sessionId(): string {
  if (!hasWindow) return 'ssr'
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = uuid()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return 'session'
  }
}

function attribution(): AnalyticsProps {
  const out: AnalyticsProps = { ...captureFirstTouchAttribution() }
  if (!hasWindow) return out
  try {
    const q = new URLSearchParams(window.location.search)
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
      const v = q.get(k)
      if (v && out[k] == null) out[k] = v
    }
  } catch {
    /* ignore malformed query */
  }
  return normalizeLegacyAttribution(out)
}

function beacon(body: string): void {
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain' })
      if (navigator.sendBeacon(ENVELOPE_URL, blob)) return
    }
    fetch(ENVELOPE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body,
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* never throw from analytics */
  }
}

function replayId(): string | undefined {
  try {
    return Sentry.getReplay()?.getReplayId() || undefined
  } catch {
    return undefined
  }
}

export function createAnalytics(source: AnalyticsSource): ProductAnalytics {
  let currentUserId: string | null = null
  let currentUserInternal = false
  // Synchronize the two parent-domain cookies before the first event is sent.
  captureFirstTouchAttribution()
  getAnonymousId()

  function envelope(name: string, props: AnalyticsProps): string {
    const eventId = uuid()
    const header = { event_id: eventId, sent_at: new Date().toISOString() }
    const itemHeader = { type: 'analytics' }
    const payload = {
      event_id: eventId,
      name,
      source,
      ts: Math.floor(Date.now() / 1000),
      anon_id: getAnonymousId(),
      session_id: sessionId(),
      user_id: currentUserId,
      path: hasWindow ? window.location.pathname : undefined,
      referrer: hasWindow ? document.referrer || undefined : undefined,
      release: (import.meta as any).env?.VITE_RELEASE,
      environment: (import.meta as any).env?.MODE || 'production',
      props: {
        ...attribution(),
        replay_id: replayId(),
        ...(currentUserInternal ? { internal: true } : {}),
        ...props,
      },
    }
    return `${JSON.stringify(header)}\n${JSON.stringify(itemHeader)}\n${JSON.stringify(payload)}`
  }

  function identify(user: AnalyticsUser | null): void {
    currentUserId = user?.id ?? null
    currentUserInternal = isInternalEmail(user?.email)
  }

  // The Zaraz path is what reaches GA4 and, from there, the BigQuery export.
  // It used to forward neither the identity nor the internal flag that
  // `identify` records, with two consequences that only surfaced when someone
  // tried to act on the data:
  //
  //   1. Every GA4 event was anonymous. All fourteen accounts that hit the
  //      handwriting paywall came back with a null user_id, so the warmest
  //      list in the product -- people who reached a price wall on purpose --
  //      could not be contacted or joined to anything in D1.
  //   2. Operator usage could not be excluded. The envelope path stamps
  //      `internal`, GA4 never saw it, so every BigQuery figure silently
  //      included whoever was building the thing.
  //
  // Sent as `app_user_id` rather than GA4's reserved `user_id`, so it lands in
  // event_params and is queryable without depending on GA4's own identity
  // handling. It is the same opaque account id the envelope already carries --
  // no email, and nothing new about the user leaves the page.
  function zarazForward(name: string, props: AnalyticsProps): void {
    if (name === 'page_view') return
    try {
      const z = (window as any).zaraz
      if (z && typeof z.track === 'function') {
        z.track(name, {
          source,
          ...attribution(),
          ...(currentUserId ? { app_user_id: currentUserId } : {}),
          ...(currentUserInternal ? { internal: true } : {}),
          ...props,
        })
      }
    } catch {
      /* never throw from analytics */
    }
  }

  function track(name: string, props: AnalyticsProps = {}): void {
    if (!hasWindow) return
    try {
      beacon(envelope(name, props))
    } catch {
      /* never throw from analytics */
    }
    zarazForward(name, props)
  }

  function trackOnce(key: string, name: string, props: AnalyticsProps = {}): boolean {
    if (!hasWindow) return false
    try {
      const flag = `${FIRST_NOTE_KEY}:${key}`
      if (localStorage.getItem(flag)) return false
      localStorage.setItem(flag, '1')
      track(name, props)
      return true
    } catch {
      track(name, props)
      return false
    }
  }

  function trackSessionOnce(key: string, name: string, props: AnalyticsProps = {}): boolean {
    if (!hasWindow) return false
    try {
      const flag = `${SESSION_KEY}:event:${key}`
      if (sessionStorage.getItem(flag)) return false
      sessionStorage.setItem(flag, '1')
      track(name, props)
      return true
    } catch {
      track(name, props)
      return false
    }
  }

  return { identify, track, trackOnce, trackSessionOnce }
}
